/**
 * API client functions for Project Management
 */

import {
  Project,
  ProjectWithDetails,
  ProjectCreate,
  ProjectUpdate,
  ProjectResponse,
  ProjectListResponse,
  ProjectUser,
  ProjectUserAdd,
  ProjectUserUpdate,
  ProjectUserResponse,
  ProjectUsersResponse,
  ProjectRole
} from '@/types/project'
import { ProjectHistoryResponse } from '@/types/projectHistory'
import { apiClient } from './base'

/**
 * Fetch all projects for current user
 */
export async function fetchProjects(
  skip: number = 0,
  limit: number = 100
): Promise<ProjectListResponse> {
  return apiClient.get(`/api/projects?skip=${skip}&limit=${limit}`)
}

/**
 * Fetch a single project by ID
 * @param id - Project ID
 * @param includeSteps - Optional array of step numbers or 'all' to control which data is loaded
 *                       Examples: [7, 8] loads only Steps 7-8, 'all' loads everything
 */
export async function fetchProject(id: string, includeSteps?: number[] | 'all'): Promise<ProjectResponse> {
  const params = new URLSearchParams()
  if (includeSteps) {
    params.append('include_steps', Array.isArray(includeSteps) ? includeSteps.join(',') : 'all')
  }
  
  const url = `/api/projects/${id}${params.toString() ? '?' + params.toString() : ''}`
  return apiClient.get(url)
}

/**
 * Create a new project
 */
export async function createProject(data: ProjectCreate): Promise<ProjectResponse> {
  return apiClient.post(`/api/projects`, data)
}

/**
 * Update an existing project
 */
export async function updateProject(
  id: string,
  data: ProjectUpdate
): Promise<ProjectResponse> {
  return apiClient.put(`/api/projects/${id}`, data)
}

/**
 * Delete a project
 */
export async function deleteProject(id: string): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`/api/projects/${id}`)
}

/**
 * Fetch all team members for a project
 */
export async function fetchProjectUsers(projectId: string): Promise<ProjectUsersResponse> {
  return apiClient.get(`/api/projects/${projectId}/users`)
}

/**
 * Add a user to a project
 */
export async function addProjectUser(
  projectId: string,
  userId: string,
  role: ProjectRole
): Promise<ProjectUserResponse> {
  return apiClient.post(`/api/projects/${projectId}/users`, {
    user_id: userId,
    role
  })
}

/**
 * Update a user's role in a project
 */
export async function updateProjectUserRole(
  projectId: string,
  userId: string,
  role: ProjectRole
): Promise<ProjectUserResponse> {
  return apiClient.patch(`/api/projects/${projectId}/users/${userId}`, { role })
}

/**
 * Remove a user from a project
 */
export async function removeProjectUser(
  projectId: string,
  userId: string
): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`/api/projects/${projectId}/users/${userId}`)
}

/**
 * Advance project to next step
 */
export async function advanceStep(projectId: string): Promise<{ success: boolean; message: string }> {
  return apiClient.post(`/api/projects/${projectId}/advance-step`)
}

/**
 * Clear all step data for a project
 */
export async function clearProjectSteps(projectId: string): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`/api/projects/${projectId}/steps`)
}

/**
 * Mark a step as finalized (user clicked "Proceed to Next Step")
 */
export async function finalizeStep(
  projectId: string,
  stepNumber: number
): Promise<{ success: boolean; message: string; step_number: number }> {
  return apiClient.post(`/api/projects/${projectId}/finalize-step/${stepNumber}`)
}

/**
 * Check if data exists after a specific step
 */
export async function checkDataExistsAfterStep(
  projectId: string,
  stepNumber: number
): Promise<{ success: boolean; has_data: boolean; steps_with_data: number[] }> {
  return apiClient.get(`/api/projects/${projectId}/steps/check-data-after/${stepNumber}`)
}

/**
 * Clear all data from steps after the specified step
 * 
 * @param confirmed - If true, confirms deletion of data. If false, backend checks first.
 */
export async function clearStepsAfter(
  projectId: string,
  stepNumber: number,
  confirmed: boolean = false
): Promise<{ 
  success: boolean; 
  message: string; 
  cleared_steps: number[]; 
  cleared_counts?: Record<string, number>;
  status?: string;
  requires_confirmation?: boolean;
  steps_with_data?: number[];
}> {
  return apiClient.post(`/api/projects/${projectId}/steps/clear-after/${stepNumber}?confirmed=${confirmed}`)
}

// Step 1 CRUD Operations

export interface Step1DataResponse {
  success: boolean
  step1_data: Record<string, any>
  message?: string
}

/**
 * Update strategic context fields in Step 1
 */
export async function updateStrategicContext(
  projectId: string,
  data: {
    narrative?: string
    industry_context?: string
    market_position?: string
    strategic_goals?: string[]
    assumptions_made?: string[]
  }
): Promise<Step1DataResponse> {
  return apiClient.put(`/api/projects/${projectId}/step1/strategic-context`, data)
}

/**
 * Add a new challenge to Step 1
 */
export async function addChallenge(
  projectId: string,
  challenge: {
    challenge_name: string
    description: string
    segments?: string[]
    locus: string
    importance?: number
    trend?: string
    evidence_source?: string
    related_values?: string[]
    assumptions?: string[]
    dependencies?: string[]
    notes?: string
  }
): Promise<Step1DataResponse> {
  return apiClient.post(`/api/projects/${projectId}/step1/challenges`, challenge)
}

/**
 * Update an existing challenge in Step 1
 */
export async function updateChallenge(
  projectId: string,
  challengeId: string,
  data: {
    challenge_name?: string
    description?: string
    segments?: string[]
    locus?: string
    importance?: number
    trend?: string
    evidence_source?: string
    related_values?: string[]
    assumptions?: string[]
    dependencies?: string[]
    notes?: string
  }
): Promise<Step1DataResponse> {
  return apiClient.put(`/api/projects/${projectId}/step1/challenges/${challengeId}`, data)
}

/**
 * Delete a challenge from Step 1
 */
export async function deleteChallenge(
  projectId: string,
  challengeId: string
): Promise<Step1DataResponse> {
  return apiClient.delete(`/api/projects/${projectId}/step1/challenges/${challengeId}`)
}

/**
 * Add a new customer value to Step 1
 */
export async function addCustomerValue(
  projectId: string,
  value: {
    label: string
    category: string
    description: string
    segments?: string[]
    evidence_source?: string
    related_challenges?: string[]
    assumptions?: string[]
    notes?: string
  }
): Promise<Step1DataResponse> {
  return apiClient.post(`/api/projects/${projectId}/step1/customer-values`, value)
}

/**
 * Update an existing customer value
 */
export async function updateCustomerValue(
  projectId: string,
  valueId: string,
  data: {
    label?: string
    category?: string
    description?: string
    segments?: string[]
    evidence_source?: string
    related_challenges?: string[]
    assumptions?: string[]
    notes?: string
  }
): Promise<Step1DataResponse> {
  return apiClient.put(`/api/projects/${projectId}/customer-values/${valueId}`, data)
}

/**
 * Delete a customer value
 */
export async function deleteCustomerValue(
  projectId: string,
  valueId: string
): Promise<Step1DataResponse> {
  return apiClient.delete(`/api/projects/${projectId}/customer-values/${valueId}`)
}

/**
 * Finalize Step 1 data and clear subsequent steps
 */
export async function finalizeStep1(
  projectId: string,
  step1Data: Record<string, any>
): Promise<Step1DataResponse> {
  return apiClient.post(`/api/projects/${projectId}/step1/finalize`, { step1_data: step1Data })
}

/**
 * Update project step context (extra context)
 */
export async function updateProjectStepContext(
  projectId: string,
  step: number,
  context: string
): Promise<Project> {
  return apiClient.put(`/api/projects/${projectId}/step-context/${step}`, { context })
}

// Step 2 CRUD API Functions

export interface Step2DataResponse {
  success: boolean
  step2_data: Record<string, any>
  message?: string
}

/**
 * Add a new prerequisite to Step 2
 */
export async function addPrerequisite(
  projectId: string,
  data: {
    value_id: string
    statement: string
    essentiality: string
    evidence_source?: string
    notes?: string
  }
): Promise<Step2DataResponse> {
  return apiClient.post(`/api/projects/${projectId}/step2/prerequisites`, data)
}

/**
 * Add a new prerequisite (with embedded opposite state)
 */
export async function addPrerequisitePair(
  projectId: string,
  data: {
    value_id: string
    statement: string
    essentiality: string
    evidence_source?: string
    opp_transformation: string
    opp_statement: string
    opp_rationale: string
    notes?: string
  }
): Promise<any> {
  return apiClient.post(`/api/projects/${projectId}/prerequisites`, data)
}

/**
 * Update an existing prerequisite in Step 2
 */
export async function updatePrerequisite(
  projectId: string,
  prereqId: string,
  data: {
    value_id?: string
    statement?: string
    essentiality?: string
    evidence_source?: string
    notes?: string
  }
): Promise<Step2DataResponse> {
  return apiClient.put(`/api/projects/${projectId}/step2/prerequisites/${prereqId}`, data)
}

/**
 * Update a prerequisite (with embedded opposite state)
 */
export async function updatePrerequisitePair(
  projectId: string,
  prereqId: string,
  data: {
    value_id?: string
    statement?: string
    essentiality?: string
    evidence_source?: string
    opp_transformation?: string
    opp_statement?: string
    opp_rationale?: string
    notes?: string
  }
): Promise<any> {
  return apiClient.put(`/api/projects/${projectId}/prerequisites/${prereqId}`, data)
}

/**
 * Delete a prerequisite (with embedded opposite state) from Step 4
 */
export async function deletePrerequisite(
  projectId: string,
  prereqId: string
): Promise<Step2DataResponse> {
  return apiClient.delete(`/api/projects/${projectId}/prerequisites/${prereqId}`)
}

/**
 * Toggle lock state of a prerequisite (with embedded opposite state)
 */
export async function togglePrerequisiteLock(
  projectId: string,
  prereqId: string,
  isLocked: boolean
): Promise<{ success: boolean; message: string }> {
  return apiClient.patch(`/api/projects/${projectId}/prerequisites/${prereqId}/lock`, {
    is_locked: isLocked
  })
}

/**
 * Add a new idea to Step 2 (DEPRECATED - use createIdea instead)
 */
export async function addIdea(
  projectId: string,
  data: {
    value_id: string
    opposite_state_id: string
    linked_business_challenges?: string[]
    title: string
    tagline: string
    description: string
    segments?: string[]
    image_prompt?: string
    dependencies?: string[]
    notes?: string
  }
): Promise<Step2DataResponse> {
  return apiClient.post(`/api/projects/${projectId}/step2/ideas`, data)
}

/**
 * Recalculate opposite states based on current prerequisites (streaming)
 * Returns the streaming URL - caller should handle EventSource
 */
export function getRecalculateOppositeStatesStreamUrl(projectId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
  return `${baseUrl}/api/projects/${projectId}/step2/recalculate-opposites/stream`
}

/**
 * Recalculate ideas based on current opposite states (streaming)
 * Returns the streaming URL - caller should handle EventSource
 */
export function getRecalculateIdeasStreamUrl(projectId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
  return `${baseUrl}/api/projects/${projectId}/step2/recalculate-ideas/stream`
}

/**
 * Get project history with optional filtering
 */
export async function getProjectHistory(
  projectId: string,
  options?: { step?: number; skip?: number; limit?: number }
): Promise<ProjectHistoryResponse> {
  const params = new URLSearchParams()
  if (options?.step !== undefined) params.append('step', options.step.toString())
  if (options?.skip !== undefined) params.append('skip', options.skip.toString())
  if (options?.limit !== undefined) params.append('limit', options.limit.toString())

  const queryString = params.toString()
  const url = `/api/projects/${projectId}/history${queryString ? '?' + queryString : ''}`

  try {
    return await apiClient.get(url)
  } catch (error) {
    // Return empty history on error (endpoint might not exist yet)
    console.warn('Project history endpoint not available:', error)
    return {
      success: true,
      history: [],
      total: 0,
      skip: 0,
      limit: 50
    }
  }
}

/**
 * Business Challenges Response Type
 */
export interface BusinessChallengesResponse {
  success: boolean
  challenges: Array<{
    challenge_id: string
    title: string
    description: string
    segments: string[]
    locus: string
    importance: number
    trend: string
    evidence_source: string
    related_values: string[]
    assumptions: string[]
    dependencies: string[]
    notes: string
    pest_factors: string[]
    id: string
    project_id: string
    metadata: Record<string, any>
    created_by?: string
    created_at: string
    updated_at: string
  }>
  total: number
}

/**
 * Fetch all business challenges for a project
 */
export async function fetchBusinessChallenges(projectId: string): Promise<BusinessChallengesResponse> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/business-challenges`)
  } catch (error) {
    console.error('Failed to fetch business challenges:', error)
    return { success: false, challenges: [], total: 0 }
  }
}

/**
 * Create a new business challenge
 */
export async function createBusinessChallenge(projectId: string, challengeData: any): Promise<any> {
  try {
    return await apiClient.post(`/api/projects/${projectId}/business-challenges`, challengeData)
  } catch (error) {
    console.error('Failed to create business challenge:', error)
    throw error
  }
}

/**
 * Update an existing business challenge
 */
export async function updateBusinessChallenge(projectId: string, challengeId: string, challengeData: any): Promise<any> {
  try {
    return await apiClient.put(`/api/projects/${projectId}/business-challenges/${challengeId}`, challengeData)
  } catch (error) {
    console.error('Failed to update business challenge:', error)
    throw error
  }
}

/**
 * Delete a business challenge
 */
export async function deleteBusinessChallenge(projectId: string, challengeId: string): Promise<any> {
  try {
    return await apiClient.delete(`/api/projects/${projectId}/business-challenges/${challengeId}`)
  } catch (error) {
    console.error('Failed to delete business challenge:', error)
    throw error
  }
}

/**
 * Toggle lock state of a business challenge
 */
export async function toggleBusinessChallengeLock(
  projectId: string,
  challengeId: string,
  isLocked: boolean
): Promise<any> {
  try {
    return await apiClient.patch(
      `/api/projects/${projectId}/business-challenges/${challengeId}/lock?is_locked=${isLocked}`
    )
  } catch (error) {
    console.error('Failed to toggle business challenge lock:', error)
    throw error
  }
}

/**
 * Industry Research Categories Types and Functions
 */
export interface IndustryResearchCategoryMetadata {
  is_locked: boolean
  origin: 'generated' | 'kept' | 'user'
  modified_by: 'ai' | 'user'
}

export interface IndustryResearchCategory {
  id: string  // CAT-001, CAT-002, etc.
  name: string
  items: string[]  // Plain string array
  metadata: IndustryResearchCategoryMetadata
}

export interface IndustryResearchCategoriesResponse {
  success: boolean
  categories: IndustryResearchCategory[]
  total: number
  message?: string
}

// ============================================================================
// CATEGORY-LEVEL API FUNCTIONS (NEW)
// ============================================================================

/**
 * Get all industry research categories for a project
 */
export async function getIndustryResearchCategories(projectId: string): Promise<IndustryResearchCategoriesResponse> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/industry-research-categories`)
  } catch (error) {
    console.error('Failed to fetch industry research categories:', error)
    return { success: false, categories: [], total: 0 }
  }
}

/**
 * Create a new category with optional items
 * @param projectId - Project ID
 * @param categoryName - Name of the category
 * @param items - Optional array of item texts. If empty/null, will trigger AI generation
 */
export async function createIndustryResearchCategoryWithItems(
  projectId: string,
  categoryName: string,
  items?: string[]
): Promise<any> {
  try {
    const payloadItems = items && items.length > 0 ? items : null
    return await apiClient.post(`/api/projects/${projectId}/industry-research-categories`, {
      name: categoryName,
      items: payloadItems
    })
  } catch (error) {
    console.error('Failed to create industry research category:', error)
    throw error
  }
}

/**
 * Update category name and/or items
 * @param projectId - Project ID
 * @param categoryId - Category ID (e.g., CAT-001)
 * @param newName - New category name (optional)
 * @param items - New items array (optional)
 */
export async function updateIndustryResearchCategory(
  projectId: string,
  categoryId: string,
  data: { newName?: string; items?: string[] }
): Promise<any> {
  try {
    return await apiClient.put(
      `/api/projects/${projectId}/industry-research-categories/${categoryId}`,
      {
        new_name: data.newName,
        items: data.items
      }
    )
  } catch (error) {
    console.error('Failed to update industry research category:', error)
    throw error
  }
}

/**
 * Delete an entire category
 */
export async function deleteIndustryResearchCategory(projectId: string, categoryId: string): Promise<any> {
  try {
    return await apiClient.delete(
      `/api/projects/${projectId}/industry-research-categories/${categoryId}`
    )
  } catch (error) {
    console.error('Failed to delete industry research category:', error)
    throw error
  }
}

/**
 * Toggle lock state of an entire category
 */
export async function toggleIndustryResearchCategoryLock(
  projectId: string,
  categoryId: string,
  isLocked: boolean
): Promise<any> {
  try {
    return await apiClient.patch(
      `/api/projects/${projectId}/industry-research-categories/${categoryId}/lock?is_locked=${isLocked}`
    )
  } catch (error) {
    console.error('Failed to toggle industry research category lock:', error)
    throw error
  }
}

/**
 * Create a new category (DEPRECATED - use createIndustryResearchCategoryWithItems)
 */
export async function createIndustryResearchCategory(projectId: string, categoryName: string): Promise<any> {
  try {
    return await apiClient.post(`/api/projects/${projectId}/industry-research-categories`, { name: categoryName })
  } catch (error) {
    console.error('Failed to create industry research category:', error)
    throw error
  }
}

/**
 * Customer Values Response Type
 */
export interface CustomerValuesResponse {
  success: boolean
  customer_values: Array<{
    value_id: string
    label: string
    category: string
    description: string
    segments: string[]
    evidence_source: string
    related_challenges: string[]
    assumptions: string[]
    notes: string
    id: string
    project_id: string
    metadata: Record<string, any>
    created_by?: string
    modified_by?: string
    created_at: string
    updated_at: string
  }>
  total: number
}

/**
 * Fetch all customer values for a project
 */
export async function fetchCustomerValues(projectId: string): Promise<CustomerValuesResponse> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/customer-values`)
  } catch (error) {
    console.error('Failed to fetch customer values:', error)
    return { success: false, customer_values: [], total: 0 }
  }
}

/**
 * Toggle lock state of a customer value
 */
export async function toggleCustomerValueLock(
  projectId: string,
  valueId: string,
  isLocked: boolean
): Promise<any> {
  try {
    return await apiClient.patch(
      `/api/projects/${projectId}/customer-values/${valueId}/lock?is_locked=${isLocked}`
    )
  } catch (error) {
    console.error('Failed to toggle customer value lock:', error)
    throw error
  }
}


/**
 * Ideas Response Type
 */
export interface IdeasResponse {
  success: boolean
  ideas: Array<{
    idea_id: string
    title: string
    tagline: string
    description: string
    value_id: string
    opposite_state_id: string
    linked_business_challenges: string[]
    segments: string[]
    image_prompt: string
    dependencies: string[]
    notes: string
    id: string
    project_id: string
    metadata: Record<string, any>
    created_by?: string
    modified_by?: string
    created_at: string
    updated_at: string
  }>
  total: number
}

export interface PrerequisitesResponse {
  success: boolean
  prerequisites: any[]
  total: number
}

/**
 * Fetch all prerequisites for a project
 */
export async function fetchPrerequisites(projectId: string): Promise<PrerequisitesResponse> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/prerequisites`)
  } catch (error) {
    console.error('Failed to fetch prerequisites:', error)
    return { success: false, prerequisites: [], total: 0 }
  }
}

/**
 * Fetch all ideas for a project
 */
export async function fetchIdeas(projectId: string): Promise<IdeasResponse> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/ideas`)
  } catch (error) {
    console.error('Failed to fetch ideas:', error)
    return { success: false, ideas: [], total: 0 }
  }
}

/**
 * Create a new idea
 */
export async function createIdea(
  projectId: string,
  ideaData: {
    idea_id: string
    value_id: string
    title: string
    tagline: string
    description: string
    linked_opposite_states?: string[]
    linked_prerequisites?: string[]
    linked_business_challenges?: string[]
    segments?: string[]
    image_prompt?: string
    dependencies?: string[]
    notes?: string
  }
): Promise<any> {
  try {
    return await apiClient.post(`/api/projects/${projectId}/ideas`, ideaData)
  } catch (error) {
    console.error('Failed to create idea:', error)
    throw error
  }
}

/**
 * Update an existing idea
 */
export async function updateIdea(
  projectId: string,
  ideaId: string,
  ideaData: {
    idea_id?: string
    value_id?: string
    title?: string
    tagline?: string
    description?: string
    linked_opposite_states?: string[]
    linked_prerequisites?: string[]
    linked_business_challenges?: string[]
    segments?: string[]
    image_prompt?: string
    dependencies?: string[]
    notes?: string
  }
): Promise<any> {
  try {
    return await apiClient.put(`/api/projects/${projectId}/ideas/${ideaId}`, ideaData)
  } catch (error) {
    console.error('Failed to update idea:', error)
    throw error
  }
}

/**
 * Delete an idea
 */
export async function deleteIdea(
  projectId: string,
  ideaId: string
): Promise<any> {
  try {
    return await apiClient.delete(`/api/projects/${projectId}/ideas/${ideaId}`)
  } catch (error) {
    console.error('Failed to delete idea:', error)
    throw error
  }
}

/**
 * Toggle lock state of an idea
 */
export async function toggleIdeaLock(
  projectId: string,
  ideaId: string,
  isLocked: boolean
): Promise<any> {
  try {
    return await apiClient.patch(
      `/api/projects/${projectId}/ideas/${ideaId}/lock?is_locked=${isLocked}`
    )
  } catch (error) {
    console.error('Failed to toggle idea lock:', error)
    throw error
  }
}

/**
 * Update an axis pair
 */
export async function updateAxisPair(
  projectId: string,
  axisId: string,
  data: {
    axis_x_name?: string
    axis_y_name?: string
    axis_x_description?: string
    axis_y_description?: string
    notes?: string
  }
): Promise<any> {
  try {
    return await apiClient.put(`/api/projects/${projectId}/axis-pairs/${axisId}`, data)
  } catch (error) {
    console.error('Failed to update axis pair:', error)
    throw error
  }
}

/**
 * Toggle lock state of an axis pair
 */
export async function toggleAxisPairLock(
  projectId: string,
  axisId: string,
  isLocked: boolean
): Promise<any> {
  try {
    return await apiClient.patch(
      `/api/projects/${projectId}/axis-pairs/${axisId}/lock?is_locked=${isLocked}`
    )
  } catch (error) {
    console.error('Failed to toggle axis pair lock:', error)
    throw error
  }
}

/**
 * Fetch all refined ideas for a project (Step 10)
 */
export async function fetchRefinedIdeas(projectId: string): Promise<any> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/refined-ideas`)
  } catch (error) {
    console.error('Failed to fetch refined ideas:', error)
    throw error
  }
}

/**
 * Create a new refined idea
 */
export async function createRefinedIdea(
  projectId: string,
  data: any
): Promise<any> {
  try {
    return await apiClient.post(`/api/projects/${projectId}/refined-ideas`, data)
  } catch (error) {
    console.error('Failed to create refined idea:', error)
    throw error
  }
}

/**
 * Update an existing refined idea
 */
export async function updateRefinedIdea(
  projectId: string,
  ideaId: string,
  data: any
): Promise<any> {
  try {
    return await apiClient.put(`/api/projects/${projectId}/refined-ideas/${ideaId}`, data)
  } catch (error) {
    console.error('Failed to update refined idea:', error)
    throw error
  }
}

/**
 * Delete a refined idea
 */
export async function deleteRefinedIdea(
  projectId: string,
  ideaId: string
): Promise<any> {
  try {
    return await apiClient.delete(`/api/projects/${projectId}/refined-ideas/${ideaId}`)
  } catch (error) {
    console.error('Failed to delete refined idea:', error)
    throw error
  }
}

/**
 * Toggle lock state of a refined idea
 */
export async function toggleRefinedIdeaLock(
  projectId: string,
  ideaId: string,
  isLocked: boolean
): Promise<any> {
  try {
    return await apiClient.patch(
      `/api/projects/${projectId}/refined-ideas/${ideaId}/lock?is_locked=${isLocked}`
    )
  } catch (error) {
    console.error('Failed to toggle refined idea lock:', error)
    throw error
  }
}


/**
 * Link a document to a project (Step 1 selection)
 */
export async function linkDocumentToProject(
  projectId: string,
  documentId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    return await apiClient.post(`/api/projects/${projectId}/documents/${documentId}`)
  } catch (error) {
    console.error('Failed to link document to project:', error)
    throw error
  }
}

/**
 * Unlink a document from a project
 */
export async function unlinkDocumentFromProject(
  projectId: string,
  documentId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    return await apiClient.delete(`/api/projects/${projectId}/documents/${documentId}`)
  } catch (error) {
    console.error('Failed to unlink document from project:', error)
    throw error
  }
}

/**
 * Get all documents linked to a project
 */
export async function getProjectDocuments(
  projectId: string
): Promise<{ success: boolean; documents: any[]; total: number }> {
  try {
    return await apiClient.get(`/api/projects/${projectId}/documents`)
  } catch (error) {
    console.error('Failed to get project documents:', error)
    return { success: false, documents: [], total: 0 }
  }
}

// ============================================================================
// STEP 7: AXIS PAIRS WITH VECTOR ALLOCATION
// ============================================================================

export interface Step7InitResponse {
  success: boolean
  session_id: string
  timestamp: string
  ideas_count: number
  idea_scores_count: number
}

/**
 * Initialize Step 7 streaming session
 */
export async function initStep7Stream(projectId: string): Promise<Step7InitResponse> {
  try {
    return await apiClient.post(`/api/projects/${projectId}/step7/init`, {})
  } catch (error) {
    console.error('Failed to initialize Step 7 stream:', error)
    throw error
  }
}

/**
 * Regenerate a specific axis pair (delete and recreate)
 */
export async function regenerateAxisPair(
  projectId: string,
  axisId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    // Delete the axis pair (this will cascade delete allocations)
    await apiClient.delete(`/api/projects/${projectId}/axis-pairs/${axisId}`)

    return {
      success: true,
      message: `Axis pair ${axisId} deleted. Regenerate all axis pairs to create a new one.`
    }
  } catch (error) {
    console.error('Failed to regenerate axis pair:', error)
    throw error
  }
}

// ============================================================================
// STEP 8: REFINED IDEAS STREAMING
// ============================================================================

export interface Step8InitResponse {
  success: boolean
  session_id: string
  timestamp: string
  quadrant_ideas_count: number
}

/**
 * Initialize Step 8 Refined Ideas streaming session
 */
export async function initStep8RefinedIdeasStream(
  projectId: string,
  selectedAxisId: string,
  selectedQuadrant: string,
  axisData: any,
  quadrantIdeas: any[],
  customerValues: any[],
  narrative?: string,
  keptIdeas?: any[]
): Promise<Step8InitResponse> {
  try {
    return await apiClient.post(`/api/analyze/step8-refined-ideas/init`, {
      project_id: projectId,
      selected_axis_id: selectedAxisId,
      selected_quadrant: selectedQuadrant,
      axis_data: axisData,
      quadrant_ideas: quadrantIdeas,
      customer_values: customerValues,
      narrative: narrative || '',
      kept_ideas: keptIdeas || []
    })
  } catch (error) {
    console.error('Failed to initialize Step 10 refined ideas stream:', error)
    throw error
  }
}

/**
 * Get Step 8 streaming URL
 */
export function getStep8StreamUrl(sessionId: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
  return `${baseUrl}/api/analyze/step8-refined-ideas/stream?session_id=${sessionId}`
}
